import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Lock, Save, TrendingUp, DollarSign, Image as ImageIcon, FileText, Mic, ScanSearch, Loader2, MapPin, ChevronLeft, RotateCcw, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// A transparent, spreadsheet-style list of exactly which visitor sessions are being counted.
// Replaces the old geo map (CDN geo headers aren't reliably populated, so the map showed nothing).
function VisitorList({ visitors, onClose }) {
  const rows = visitors || [];
  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + ", " +
        d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    } catch { return "—"; }
  };
  return (
    <div className="space-y-3" data-testid="visitor-list-panel">
      <button data-testid="list-back-btn" onClick={onClose} className="flex items-center gap-1 text-sm font-bold text-[#8fbf82] hover:text-[#8fbf82]">
        <ChevronLeft size={16} /> Back to traffic
      </button>
      <div className="flex items-center gap-2">
        <Users size={16} className="text-[#8fbf82]" />
        <h3 className="font-extrabold text-white text-base">Visitor sessions <span className="font-semibold text-white/45">({rows.length})</span></h3>
      </div>
      <p className="text-[11px] text-white/50 leading-relaxed">
        Every real (non-admin, non-bot) visitor session counted in this window — one row per unique visitor.
        Location is shown only when the network/CDN provides it; otherwise it reads “Unknown”.
      </p>
      {rows.length ? (
        <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: "#5d8a52" }}>
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-white/50 bg-[#14141b] border-b" style={{ borderColor: "rgba(255,255,255,.08)" }}>
            <span>Location · Source</span>
            <span className="text-right">Views</span>
            <span className="text-right">Last seen</span>
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {rows.map((v, i) => (
              <div key={v.id + i} data-testid="visitor-row"
                className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 text-xs items-center border-b" style={{ borderColor: "rgba(255,255,255,.06)" }}>
                <div className="min-w-0">
                  <div className="text-white/85 font-semibold truncate flex items-center gap-1">
                    <MapPin size={11} className={v.location === "Unknown" ? "text-white/30" : "text-[#8fbf82]"} />
                    {v.location}
                  </div>
                  <div className="text-white/40 text-[10px] truncate">from {v.referrer} · id {v.id}</div>
                </div>
                <span className="text-right font-bold text-white tabular-nums">{v.views}</span>
                <span className="text-right text-white/60 tabular-nums whitespace-nowrap">{fmt(v.last)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-white/45 py-6 text-center">No real visitor sessions recorded in this window yet.</p>
      )}
    </div>
  );
}

const PRICE_META = [
  { key: "image", label: "Per image", icon: ImageIcon, hint: "hero + each step photo" },
  { key: "text", label: "Per manual text", icon: FileText, hint: "one generation per video" },
  { key: "tts", label: "Per voice clip", icon: Mic, hint: "one per narrated step" },
  { key: "qa", label: "Per QA scan", icon: ScanSearch, hint: "optional image check" },
];

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const money4 = (n) => `$${(Number(n) || 0).toFixed(4)}`;

export function AdminCostsModal({ open, onClose }) {
  const [pass, setPass] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [prices, setPrices] = useState(null);
  // planner inputs
  const [steps, setSteps] = useState(15);
  const [narrated, setNarrated] = useState(true);
  const [assumeQa, setAssumeQa] = useState(true);
  // optional add-on cards (each = 1 text gen + 2 voice clips when narrated; no AI image — PIL-rendered)
  const [funCards, setFunCards] = useState(0);   // Fun Fact / retention cards (0–3)
  const [endQuiz, setEndQuiz] = useState(false); // end-of-video Quiz card
  const [riddle, setRiddle] = useState(false);   // "Just For Fun" Riddle card
  const [bundleSize, setBundleSize] = useState(6);    // videos per order (customer buys a pack)
  const [bundlePrice, setBundlePrice] = useState(25); // $ charged per order
  const [users, setUsers] = useState(250);
  const [perDay, setPerDay] = useState(0);
  const [perWeek, setPerWeek] = useState(4);
  // Universal Key balance watch (admin pastes their current balance from Profile → Universal Key)
  const [keyBalance, setKeyBalance] = useState(() => { try { return localStorage.getItem("sms_key_balance") || ""; } catch { return ""; } });
  const [keyBalanceTs, setKeyBalanceTs] = useState(() => { try { return localStorage.getItem("sms_key_balance_ts") || null; } catch { return null; } });
  const [keyLowDays, setKeyLowDays] = useState(() => { try { return Number(localStorage.getItem("sms_key_low_days")) || 3; } catch { return 3; } });
  const [sessionStart, setSessionStart] = useState(() => { try { return localStorage.getItem("sms_calib_session") || null; } catch { return null; } });
  const [sessWallet, setSessWallet] = useState("");
  // traffic tab
  const [tab, setTab] = useState("costs");
  const [traffic, setTraffic] = useState(null);
  const [twindow, setTwindow] = useState("last7d");
  const [tbusy, setTbusy] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [live, setLive] = useState(null);

  // Wix-style live visitor count — poll every 20s while the Traffic tab is open.
  useEffect(() => {
    if (tab !== "traffic" || !unlocked || !pass) return;
    let alive = true;
    const ping = async () => {
      try {
        const { data } = await api.post("/admin/live-visitors", { passcode: pass });
        if (alive) setLive(data.live);
      } catch { /* ignore transient errors */ }
    };
    ping();
    const id = setInterval(ping, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [tab, unlocked, pass]);

  const loadTraffic = async (win) => {
    setTbusy(true);
    try {
      const { data } = await api.post("/admin/analytics", { passcode: pass, window: win });
      setTraffic(data);
    } catch {
      toast.error("Couldn't load traffic");
    } finally {
      setTbusy(false);
    }
  };

  const resetTraffic = async () => {
    const code = window.prompt("Reset ALL traffic stats?\n\nThis clears every pageview, click and country count and cannot be undone.\n\nType the admin passcode to confirm:");
    if (!code) return;
    try {
      const { data } = await api.post("/admin/analytics/reset", { passcode: code });
      toast.success(`Traffic stats reset — cleared ${data.deleted} events.`);
      setLive(0);
      loadTraffic(twindow);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed — wrong passcode?");
    }
  };

  const reset = () => { setPass(""); setUnlocked(false); setData(null); setPrices(null); setMapOpen(false); };
  const close = () => { reset(); onClose(); };

  const load = async (passcode, aq = assumeQa, sess = sessionStart, balSince = keyBalanceTs, quiet = false) => {
    if (!quiet) setBusy(true);
    try {
      const { data } = await api.post("/admin/costs", { passcode, assume_qa: aq, session_start: sess || null, balance_since: balSince || null });
      setData(data);
      setPrices(data.prices);
      setUnlocked(true);
    } catch (e) {
      if (!quiet) toast.error(e?.response?.status === 401 ? "Incorrect admin password" : "Couldn't load — try again");
    } finally {
      if (!quiet) setBusy(false);
    }
  };

  // Balance gauge ticks down live: re-poll actual metered spend every 25s while the Costs tab is open.
  useEffect(() => {
    if (tab !== "costs" || !unlocked || !pass || !keyBalanceTs) return;
    const id = setInterval(() => { load(pass, assumeQa, sessionStart, keyBalanceTs, true); }, 25000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, unlocked, pass, keyBalanceTs, assumeQa, sessionStart]);

  // Admin (re)sets their key balance → stamp "now" so spend is counted from this moment forward.
  const setBalance = (val) => {
    setKeyBalance(val);
    const now = new Date().toISOString();
    setKeyBalanceTs(now);
    try { localStorage.setItem("sms_key_balance", val); localStorage.setItem("sms_key_balance_ts", now); } catch { /* noop */ }
    load(pass, assumeQa, sessionStart, now, true);
  };

  const startSession = () => {
    const now = new Date().toISOString();
    try { localStorage.setItem("sms_calib_session", now); } catch { /* ignore */ }
    setSessionStart(now);
    load(pass, assumeQa, now);
    toast.success("Calibration session started — make your videos, then compare to your wallet drop");
  };

  const endSession = () => {
    try { localStorage.removeItem("sms_calib_session"); } catch { /* ignore */ }
    setSessionStart(null);
    load(pass, assumeQa, null);
  };

  const savePrices = async () => {
    setSaving(true);
    try {
      await api.post("/admin/ai-prices", { passcode: pass, prices });
      toast.success("Prices saved");
      await load(pass);
    } catch {
      toast.error("Couldn't save prices");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // live per-video cost from current prices + planner inputs
  const p = prices || {};
  // optional add-on cards each cost one text generation + two voice clips (question/answer) when narrated.
  const cardCount = Math.max(0, Number(funCards)) + (endQuiz ? 1 : 0) + (riddle ? 1 : 0);
  const opImages = 1 + Number(steps);
  const opText = 1 + cardCount;
  const opVoice = narrated ? (Number(steps) + cardCount * 2) : 0;
  const opQa = assumeQa ? Number(steps) : 0;
  const liveCost =
    opImages * (p.image || 0) +
    opText * (p.text || 0) +
    opVoice * (p.tts || 0) +
    opQa * (p.qa || 0);
  // just the add-on slice, so we can show "+$0.00xx" for the extra cards
  const addonCost =
    cardCount * (p.text || 0) +
    (narrated ? cardCount * 2 * (p.tts || 0) : 0);
  // Stripe standard pricing: 2.9% + $0.30 per successful charge — applied per ORDER (a pack), not per video.
  const STRIPE_PCT = 0.029, STRIPE_FLAT = 0.30;
  const perVideoPrice = Number(bundleSize) > 0 ? Number(bundlePrice) / Number(bundleSize) : 0;
  const stripeFeeOrder = Number(bundlePrice) > 0 ? Number(bundlePrice) * STRIPE_PCT + STRIPE_FLAT : 0;
  const netOrder = Number(bundlePrice) - stripeFeeOrder;      // what actually lands in your bank
  const aiCostOrder = Number(bundleSize) * liveCost;          // Universal Key spend for the pack
  const profitOrder = netOrder - aiCostOrder;                 // true take-home per order
  const marginPct = Number(bundlePrice) > 0 ? (profitOrder / Number(bundlePrice)) * 100 : 0;
  const dayVideos = Number(users) * (Number(perDay) + Number(perWeek) / 7);
  const dayOrders = Number(bundleSize) > 0 ? dayVideos / Number(bundleSize) : 0;
  const dayGross = dayOrders * Number(bundlePrice);
  const dayStripe = dayOrders * stripeFeeOrder;
  const dayNetBank = dayGross - dayStripe;                    // hits bank after Stripe
  const dayAICost = dayVideos * liveCost;
  const dayProfit = dayNetBank - dayAICost;                   // true net after Stripe + AI
  // Universal Key runway — real last-24h burn tells us how fast the key drains
  const dailyBurn = Number(data?.actual?.windows?.last24h?.cost) || 0;
  const keyLoaded = parseFloat(keyBalance) || 0;
  const spentSince = Number(data?.actual?.spent_since_balance) || 0;
  const keyBalNum = Math.max(0, keyLoaded - spentSince);   // effective, live-declining balance
  const runwayDays = dailyBurn > 0 ? keyBalNum / dailyBurn : null;
  const keyLow = keyBalance !== "" && ((runwayDays != null && runwayDays < Number(keyLowDays)) || keyBalNum <= 0);

  return (
    createPortal(
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-0 sm:p-4 bg-black/50 overflow-y-auto" data-testid="admin-costs-modal">
      <div className="sms-tweed admin-dark w-full sm:max-w-2xl bg-[#0f0f16] sm:rounded-2xl shadow-xl my-0 sm:my-6" style={{ border: "2px solid #5d8a52", boxShadow: "0 30px 90px -20px rgba(0,0,0,.8), 0 0 40px -8px rgba(93,138,82,.35)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#2a3a26" }}>
          <h2 className="text-lg font-extrabold flex items-center gap-2 text-white"><Lock size={18} className="text-[#8fbf82]" /> Costs &amp; Margin</h2>
          <div className="flex items-center gap-2">
            {data?.stripe && (
              <span
                data-testid="stripe-mode-badge"
                title={`Stripe platform: ${data.stripe.platform} · marketplace: ${data.stripe.marketplace}`}
                className="text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full tracking-wide"
                style={data.stripe.platform === "live" && data.stripe.marketplace === "live"
                  ? { background: "#16241040", color: "#8fbf82", border: "1px solid #5d8a52" }
                  : { background: "#2a230d", color: "#f5c451", border: "1px solid #7a5f1a" }}
              >
                {data.stripe.platform === "live" && data.stripe.marketplace === "live" ? "● Stripe LIVE" : "● Stripe TEST"}
              </span>
            )}
            <button data-testid="admin-costs-close" onClick={close} className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"><X size={18} /></button>
          </div>
        </div>

        {!unlocked ? (
          <form
            className="p-8 flex flex-col items-center gap-4"
            onSubmit={(e) => { e.preventDefault(); if (pass.trim()) load(pass.trim()); }}
          >
            <div className="w-16 h-16 rounded-2xl bg-[#14141b] border-2 border-[#5d8a52] flex items-center justify-center" style={{ boxShadow: "0 0 26px -4px rgba(143,191,130,.5)" }}>
              <Lock size={30} className="text-[#8fbf82]" />
            </div>
            <p className="text-base text-white/70 text-center">Admin only. Re-enter your admin password to view AI costs.</p>
            <input
              data-testid="admin-costs-pass"
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="Admin password"
              className="sms-input w-full max-w-xs px-4 py-3 text-center text-white"
              autoFocus
            />
            <button data-testid="admin-costs-unlock" disabled={busy || !pass.trim()} className="bg-[#4f7746] hover:bg-[#3f6238] text-white px-7 py-3 rounded-full font-extrabold disabled:opacity-50 flex items-center gap-2 transition-transform hover:scale-[1.02]">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Unlock
            </button>
          </form>
        ) : (
          <div className="max-h-[85vh] overflow-y-auto">
            <div className="flex gap-1 px-5 pt-3 border-b" style={{ borderColor: "#2a3a26" }}>
              {[["costs", "Costs & Margin"], ["traffic", "Traffic"]].map(([t, label]) => (
                <button
                  key={t}
                  data-testid={`tab-${t}`}
                  onClick={() => { setTab(t); setMapOpen(false); if (t === "traffic" && !traffic) loadTraffic(twindow); }}
                  className={`text-sm font-bold px-3 py-2 rounded-t-lg transition-colors ${tab === t ? "text-[#8fbf82] border-b-2 border-[#8fbf82]" : "text-white/45 hover:text-white/70"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {tab === "costs" ? (
            <div className="p-5 space-y-5">
            {/* 0. Universal Key balance watch — live-declining gauge, flashing red hazard when low */}
            <section
              data-testid="key-balance-card"
              className={`rounded-xl p-4 ${keyLow ? "sms-hazard-flash" : ""}`}
              style={{ border: `2px solid ${keyLow ? "#e0455c" : "#5d8a52"}`, background: keyLow ? "#2a0f13" : "#14141b" }}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className={`font-bold text-sm flex items-center gap-1.5 ${keyLow ? "text-[#ff8a97]" : "text-white"}`}>
                  <DollarSign size={15} className={keyLow ? "text-[#ff8a97]" : "text-[#8fbf82]"} /> Universal Key balance
                </h3>
                <span className="text-[10px] font-bold text-[#8fbf82]" title="Add balance in Profile → Universal Key">Profile → Universal Key to top up</span>
              </div>
              {keyBalance !== "" && (
                <div className="flex items-baseline gap-3 mb-2">
                  <span className={`text-4xl font-extrabold tabular-nums ${keyLow ? "text-[#ff6b7f]" : "text-[#b8e6a6]"}`} data-testid="key-effective-balance">{money(keyBalNum)}</span>
                  <span className="text-[11px] text-white/45">est. left · loaded {money(keyLoaded)} · spent {money(spentSince)} since</span>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs mb-2">
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  Loaded $
                  <input
                    data-testid="key-balance-input"
                    type="number" step="1" min="0" value={keyBalance}
                    onChange={(e) => setBalance(e.target.value)}
                    placeholder="0.00"
                    className="w-24 px-2 py-1 rounded bg-white/10 text-white text-center outline-none font-bold" />
                </label>
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  Warn under
                  <input
                    data-testid="key-low-days"
                    type="number" step="1" min="1" value={keyLowDays}
                    onChange={(e) => { const v = parseInt(e.target.value) || 1; setKeyLowDays(v); try { localStorage.setItem("sms_key_low_days", String(v)); } catch { /* noop */ } }}
                    className="w-14 px-2 py-1 rounded bg-white/10 text-white text-center outline-none" />
                  days
                </label>
              </div>
              {keyLow ? (
                <div className="rounded-lg px-3 py-2.5 flex items-center gap-2" style={{ background: "#3a0d13" }} data-testid="key-low-banner">
                  <span className="text-xl sms-hazard-text" role="img" aria-label="hazard">⚠️</span>
                  <div className="min-w-0">
                    <div className="text-sm font-extrabold text-[#ff6b7f] uppercase tracking-wide sms-hazard-text">Low balance</div>
                    <div className="text-[11px] text-[#ffb4bd]">
                      {keyBalNum <= 0 ? "Your key is empty — renders will start failing. Top up now." : `~${runwayDays.toFixed(1)} days left at your last-24h burn (${money(dailyBurn)}/day). Top up your Universal Key now.`}
                    </div>
                  </div>
                </div>
              ) : keyBalance !== "" ? (
                <p className="text-[11px] text-[#9fb595]" data-testid="key-ok-note">
                  {runwayDays != null
                    ? <>≈ <b className="text-[#b8e6a6]">{runwayDays.toFixed(1)} days</b> of runway at your last-24h burn ({money(dailyBurn)}/day) — ticking down live as videos are made. You're clear.</>
                    : <>No spend in the last 24h yet — runway looks healthy. The balance ticks down live as new videos are created.</>}
                </p>
              ) : (
                <p className="text-[11px] text-white/45">Enter your current Universal Key balance — it then <b className="text-[#8fbf82]">declines live</b> as videos are created (using real metered spend since you set it). Flashes red when you're within your warning window. Re-enter it after each top-up.</p>
              )}
            </section>

            {/* 1. LIVE NOW — real metered snapshot (hero) */}
            <section className="rounded-xl p-4" style={{ background: "#0f1a0c", border: "2px solid #5d8a52" }} data-testid="live-now-section">
              <h3 className="font-bold text-sm text-white mb-3 flex items-center gap-1.5"><TrendingUp size={15} className="text-[#b8e6a6]" /> Live now — real Universal-Key spend</h3>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg py-3" style={{ background: "#1c3016" }} data-testid="live-24h">
                  <div className="text-[10px] font-bold text-[#9fb595] uppercase">Last 24h</div>
                  <div className="text-2xl font-extrabold text-[#b8e6a6]">{money(data?.actual?.windows?.last24h?.cost)}</div>
                  <div className="text-[9px] text-[#7f9a74]">{data?.actual?.windows?.last24h?.counts?.image || 0} img · {data?.actual?.windows?.last24h?.counts?.tts || 0} voice</div>
                </div>
                <div className="rounded-lg py-3" style={{ background: "#1c3016" }} data-testid="live-alltime">
                  <div className="text-[10px] font-bold text-[#9fb595] uppercase">All-time</div>
                  <div className="text-2xl font-extrabold text-[#b8e6a6]">{money(data?.actual?.windows?.all?.cost)}</div>
                  <div className="text-[9px] text-[#7f9a74]">{data?.actual?.total_ops || 0} ops logged</div>
                </div>
                <div className="rounded-lg py-3" style={{ background: "#1c3016" }}>
                  <div className="text-[10px] font-bold text-[#9fb595] uppercase">Avg / video</div>
                  <div className="text-2xl font-extrabold text-white" data-testid="actual-avg-video">{money4(data?.actual?.per_video?.avg_cost)}</div>
                  <div className="text-[9px] text-[#7f9a74]">{data?.actual?.per_video?.videos_counted || 0} videos</div>
                </div>
              </div>
              <p className="text-[10px] text-[#7f9a74] mt-2 leading-relaxed">Real AI operations counted as they happen — includes retries, remixes &amp; thumbnails. Video rendering &amp; storage run on your server, not the key.</p>
            </section>

            {/* 2. Calibration session — priority position */}
            <section className="rounded-xl p-4" style={{ background: "#1c3016", border: "1px dashed #4f7746" }} data-testid="calib-session-card">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="text-sm font-bold text-[#b8e6a6] flex items-center gap-1.5"><ScanSearch size={15} /> Session calibration</h3>
                {sessionStart ? (
                  <button data-testid="session-end-btn" onClick={endSession} className="text-[10px] font-bold text-[#ffb4a2] border border-[#ffb4a2]/50 rounded-full px-2.5 py-1 hover:bg-[#ffb4a2]/10 transition-colors">End session</button>
                ) : (
                  <button data-testid="session-start-btn" onClick={startSession} className="text-xs font-bold text-[#0f1a0c] bg-[#b8e6a6] rounded-full px-4 py-1.5 hover:bg-[#a3d98f] transition-colors">Start session</button>
                )}
              </div>
              {!sessionStart ? (
                <p className="text-[11px] text-[#9fb595] leading-relaxed">Jot down your Universal Key balance, tap <b className="text-[#b8e6a6]">Start session</b>, make a batch of videos, then watch the live creation numbers below. Compare the modeled cost to how much your wallet actually dropped to confirm reality.</p>
              ) : (() => {
                const s = data?.actual?.session;
                const c = s?.counts || {};
                const started = new Date(sessionStart);
                const drop = parseFloat(sessWallet) || 0;
                const ratio = (s?.cost > 0 && drop > 0) ? drop / s.cost : null;
                return (
                  <div data-testid="session-live">
                    <p className="text-[10px] text-[#7f9a74] mb-2">Live since {started.toLocaleString()}</p>
                    <div className="grid grid-cols-3 gap-2 text-center mb-2">
                      <div className="rounded-lg py-2.5" style={{ background: "#0f1a0c" }}><div className="text-[9px] text-[#9fb595] uppercase font-bold">Videos</div><div className="text-xl font-extrabold text-white" data-testid="session-videos">{s?.videos_counted || 0}</div></div>
                      <div className="rounded-lg py-2.5" style={{ background: "#0f1a0c" }}><div className="text-[9px] text-[#9fb595] uppercase font-bold">Modeled cost</div><div className="text-xl font-extrabold text-[#b8e6a6]" data-testid="session-cost">{money4(s?.cost)}</div></div>
                      <div className="rounded-lg py-2.5" style={{ background: "#0f1a0c" }}><div className="text-[9px] text-[#9fb595] uppercase font-bold">Avg / video</div><div className="text-xl font-extrabold text-white">{money4(s?.avg_cost)}</div></div>
                    </div>
                    <p className="text-[9px] text-[#7f9a74] text-center mb-2">{c.image || 0} img · {c.text || 0} txt · {c.tts || 0} voice · {c.qa || 0} qa · {s?.ops || 0} total ops</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-[#cfe0c6] border-t border-white/10 pt-2">
                      <span className="font-semibold text-[10px]">Wallet actually dropped $</span>
                      <input data-testid="session-wallet-input" type="number" step="0.01" min="0" value={sessWallet} onChange={(e) => setSessWallet(e.target.value)} placeholder="0.00" className="w-20 px-2 py-1 rounded bg-white/10 text-white text-center outline-none" />
                      {ratio && <span className="text-[10px] text-[#9fb595]" data-testid="session-ratio">Reality is <b className="text-white">{ratio.toFixed(2)}×</b> the model — multiply your unit prices by ~{ratio.toFixed(2)} to match.</span>}
                    </div>
                  </div>
                );
              })()}
            </section>

            {/* 3. Cost per video */}
            <section className="rounded-xl p-4" style={{ background: "#14141b" }}>
              <h3 className="font-bold text-sm text-white mb-2 flex items-center gap-1.5"><DollarSign size={15} className="text-[#8fbf82]" /> Cost per video</h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs mb-3">
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  Steps
                  <input data-testid="planner-steps" type="number" min="1" max="30" value={steps} onChange={(e) => setSteps(Math.max(1, parseInt(e.target.value) || 1))} className="w-14 sms-input px-2 py-1 text-center" />
                </label>
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  <input type="checkbox" checked={narrated} onChange={(e) => setNarrated(e.target.checked)} className="accent-[#4f7746]" /> Narrated video
                </label>
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  <input type="checkbox" checked={assumeQa} onChange={(e) => { setAssumeQa(e.target.checked); load(pass, e.target.checked); }} className="accent-[#4f7746]" /> Include QA scans
                </label>
              </div>
              {/* Optional add-on cards — each adds a text gen + (when narrated) 2 voice clips */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs mb-3 pt-2" style={{ borderTop: "1px dashed #2a2a35" }}>
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#8fbf82] w-full">Optional cards</span>
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  Fun Fact cards
                  <input data-testid="planner-funcards" type="number" min="0" max="3" value={funCards} onChange={(e) => setFunCards(Math.min(3, Math.max(0, parseInt(e.target.value) || 0)))} className="w-14 sms-input px-2 py-1 text-center" />
                </label>
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  <input data-testid="planner-quiz" type="checkbox" checked={endQuiz} onChange={(e) => setEndQuiz(e.target.checked)} className="accent-[#4f7746]" /> End Quiz
                </label>
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  <input data-testid="planner-riddle" type="checkbox" checked={riddle} onChange={(e) => setRiddle(e.target.checked)} className="accent-[#4f7746]" /> Riddle card
                </label>
              </div>
              <div className="text-3xl font-extrabold text-white" data-testid="live-cost">{money4(liveCost)}</div>
              <p className="text-[11px] text-white/55 mt-0.5">
                {opImages} images · {opText} text · {opVoice} voice · {opQa} QA
              </p>
              {cardCount > 0 && (
                <p className="text-[11px] text-[#8fbf82] mt-1" data-testid="addon-cost-note">
                  ↳ incl. {[funCards > 0 ? `${funCards} Fun Fact` : null, endQuiz ? "Quiz" : null, riddle ? "Riddle" : null].filter(Boolean).join(" · ")} = <b>+{money4(addonCost)}</b>/video ({cardCount} text{narrated ? ` + ${cardCount * 2} voice` : ""})
                </p>
              )}
              <p className="text-[10px] text-white/40 mt-1">Uploaded videos cost only storage — no AI spend — unless you generate an AI thumbnail for one.</p>
              <p className="text-[11px] text-white/55 mt-1">Your library average: <b>{money4(data?.per_video?.avg_cost)}</b> across {data?.per_video?.videos_counted || 0} videos (~{data?.per_video?.avg_steps || 0} steps each).</p>
            </section>

            {/* 4. What you charge & keep — Stripe-aware */}
            <section className="rounded-xl p-4" style={{ background: "#14141b", border: "2px solid #5d8a52" }} data-testid="charge-keep-section">
              <h3 className="font-bold text-sm text-white mb-2 flex items-center gap-1.5"><TrendingUp size={15} className="text-[#8fbf82]" /> What you charge &amp; keep</h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs mb-3">
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  Pack size (videos)
                  <input data-testid="bundle-size" type="number" min="1" value={bundleSize} onChange={(e) => setBundleSize(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 sms-input px-2 py-1 text-center" />
                </label>
                <label className="flex items-center gap-1.5 font-semibold text-white/70">
                  Pack price $
                  <input data-testid="bundle-price" type="number" step="0.5" min="0" value={bundlePrice} onChange={(e) => setBundlePrice(parseFloat(e.target.value) || 0)} className="w-20 sms-input px-2 py-1 text-center" />
                </label>
                <span className="text-[11px] text-white/45">= <b className="text-white">{money(perVideoPrice)}</b> / video</span>
              </div>
              {/* the money math — exactly what hits your account, per order */}
              <div className="rounded-lg overflow-hidden" style={{ border: "1px solid #2a3a26" }} data-testid="order-math">
                <div className="flex items-center justify-between px-3 py-2 bg-[#0f1a0c]">
                  <span className="text-xs text-white/70">Customer pays (per pack)</span>
                  <span className="text-sm font-extrabold text-white">{money(bundlePrice)}</span>
                </div>
                <div className="flex items-center justify-between px-3 py-2" style={{ background: "#241316" }}>
                  <span className="text-xs text-[#ffb4a2]">− Stripe fee <span className="text-white/40">(2.9% + $0.30)</span></span>
                  <span className="text-sm font-bold text-[#ffb4a2]">−{money(stripeFeeOrder)}</span>
                </div>
                <div className="flex items-center justify-between px-3 py-2 bg-[#14141b]">
                  <span className="text-xs font-bold text-white">= Lands in your bank</span>
                  <span className="text-sm font-extrabold text-white" data-testid="net-to-bank">{money(netOrder)}</span>
                </div>
                <div className="flex items-center justify-between px-3 py-2" style={{ background: "#241316" }}>
                  <span className="text-xs text-[#ffb4a2]">− AI cost <span className="text-white/40">({bundleSize} × {money4(liveCost)})</span></span>
                  <span className="text-sm font-bold text-[#ffb4a2]">−{money(aiCostOrder)}</span>
                </div>
                <div className="flex items-center justify-between px-3 py-2.5" style={{ background: profitOrder >= 0 ? "#1c3016" : "#2a1416" }}>
                  <span className="text-xs font-bold text-[#b8e6a6]">= Your profit / pack</span>
                  <span className={`text-lg font-extrabold ${profitOrder >= 0 ? "text-[#b8e6a6]" : "text-[#f0736f]"}`} data-testid="profit-per-order">{money(profitOrder)} <span className="text-[10px] font-normal text-white/45">· {marginPct.toFixed(0)}% margin</span></span>
                </div>
              </div>
            </section>

            {/* 5. At scale — Stripe-aware */}
            <section className="rounded-xl p-4" style={{ background: "#171426" }}>
              <h3 className="font-bold text-sm text-white mb-2">At scale</h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs mb-3 text-[#cfe0c6]">
                <label className="flex items-center gap-1.5 font-semibold">
                  Users
                  <input data-testid="scale-users" type="number" min="1" value={users} onChange={(e) => setUsers(parseInt(e.target.value) || 0)} className="w-20 px-2 py-1 rounded bg-white/10 text-white text-center outline-none" />
                </label>
                <label className="flex items-center gap-1.5 font-semibold">
                  Videos/user/day
                  <input data-testid="scale-perday" type="number" min="0" value={perDay} onChange={(e) => setPerDay(parseInt(e.target.value) || 0)} className="w-16 px-2 py-1 rounded bg-white/10 text-white text-center outline-none" />
                </label>
                <label className="flex items-center gap-1.5 font-semibold">
                  Videos/user/week
                  <input data-testid="scale-perweek" type="number" min="0" value={perWeek} onChange={(e) => setPerWeek(parseInt(e.target.value) || 0)} className="w-16 px-2 py-1 rounded bg-white/10 text-white text-center outline-none" />
                </label>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-white mb-2">
                <div><div className="text-[10px] text-[#9fb595] uppercase font-bold">Videos/day</div><div className="text-lg font-extrabold">{dayVideos.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
                <div><div className="text-[10px] text-[#9fb595] uppercase font-bold">Gross/day</div><div className="text-lg font-extrabold text-[#b8e6a6]" data-testid="scale-gross">{money(dayGross)}</div></div>
                <div><div className="text-[10px] text-[#9fb595] uppercase font-bold">Stripe fees/day</div><div className="text-lg font-extrabold text-[#ffb4a2]">−{money(dayStripe)}</div></div>
                <div><div className="text-[10px] text-[#9fb595] uppercase font-bold">To bank/day</div><div className="text-lg font-extrabold text-white" data-testid="scale-net-bank">{money(dayNetBank)}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center text-white">
                <div><div className="text-[10px] text-[#9fb595] uppercase font-bold">AI cost/day</div><div className="text-lg font-extrabold text-[#ffb4a2]">−{money(dayAICost)}</div></div>
                <div className="rounded-lg py-1" style={{ background: dayProfit >= 0 ? "#1c3016" : "#2a1416" }}><div className="text-[10px] text-[#9fb595] uppercase font-bold">True net/day</div><div className={`text-lg font-extrabold ${dayProfit >= 0 ? "text-[#b8e6a6]" : "text-[#f0736f]"}`} data-testid="scale-profit">{money(dayProfit)}</div></div>
              </div>
              <p className="text-[11px] text-[#9fb595] mt-2">≈ {money(dayGross * 30)} gross · −{money(dayStripe * 30)} Stripe · −{money(dayAICost * 30)} AI = <b className="text-[#b8e6a6]">{money(dayProfit * 30)} true net</b> / month.</p>
            </section>

            {/* 6. Your AI unit costs (editable config) */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm text-white">Your AI unit costs (USD)</h3>
                <button data-testid="admin-costs-save-prices" onClick={savePrices} disabled={saving} className="text-xs font-bold text-white bg-[#4f7746] hover:bg-[#3f6238] rounded-full px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {PRICE_META.map(({ key, label, icon: Icon, hint }) => (
                  <label key={key} className="rounded-xl border-2 px-3 py-2" style={{ borderColor: "#5d8a52" }}>
                    <span className="flex items-center gap-1.5 text-xs font-bold text-white/70"><Icon size={13} className="text-[#5d8a52]" /> {label}</span>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-white text-sm font-bold">$</span>
                      <input
                        data-testid={`price-${key}`}
                        type="number" step="0.001" min="0"
                        value={p[key] ?? 0}
                        onChange={(e) => setPrices({ ...prices, [key]: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-transparent text-sm font-bold text-white outline-none"
                      />
                    </div>
                    <span className="text-[10px] text-white/45">{hint}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-white/45 mt-2 leading-relaxed">Market reference (mid-2026): Gemini image ≈ $0.039 · OpenAI TTS ≈ $0.006/step · Claude text (Haiku) ≈ $0.01. Your saved rates carry a safety cushion — use <b className="text-[#8fbf82]">Session calibration</b> above to tune them to your real wallet drop.</p>
            </section>
            </div>
            ) : (
            <div className="p-5 space-y-5">
              <div className="flex items-center gap-1.5">
                {[["24h", "last24h"], ["7 days", "last7d"], ["30 days", "last30d"], ["All", "all"]].map(([l, w]) => (
                  <button key={w} data-testid={`traffic-win-${w}`} onClick={() => { setTwindow(w); loadTraffic(w); }}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 border-[#4f7746] transition-colors ${twindow === w ? "bg-[#4f7746] text-white" : "bg-transparent text-[#8fbf82] hover:bg-[#5d8a52]/15"}`}>{l}</button>
                ))}
                {tbusy && <Loader2 size={14} className="animate-spin text-[#8fbf82]" />}
                <button data-testid="traffic-reset-btn" onClick={resetTraffic} title="Reset all traffic stats (requires passcode)"
                  className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full border-2 border-[#c14b4b] bg-transparent text-[#f0736f] hover:bg-[#c14b4b]/15 transition-colors">
                  <RotateCcw size={12} /> Reset
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <div className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#3b5bd0] bg-[#141b2e] px-3 py-1.5" data-testid="admin-visits-pill" title="Your own admin visits — excluded from the visitor counts below">
                    <span className="text-[10px] font-bold text-[#8ea6ff] uppercase tracking-wide">Admin Visits</span>
                    <span className="text-base font-extrabold text-white tabular-nums" data-testid="admin-visits-count">{traffic ? (traffic.kpis.your_own_views ?? 0) : "…"}</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#c14b4b] bg-[#241316] px-3 py-1.5" data-testid="live-visitors-pill" title="Unique visitors active on your site in the last 5 minutes">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[#e0455c] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#e0455c]" />
                    </span>
                    <span className="text-[10px] font-bold text-[#f0736f] uppercase tracking-wide">Live</span>
                    <span className="text-base font-extrabold text-white tabular-nums" data-testid="live-visitors-count">{live == null ? "…" : live}</span>
                  </div>
                </div>
              </div>

              {!traffic ? (
                <p className="text-sm text-white/55 py-8 text-center">Loading traffic…</p>
              ) : mapOpen ? (
                <VisitorList visitors={traffic.visitors} onClose={() => setMapOpen(false)} />
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2.5 text-center">
                    {[
                      ["Unique visitors", traffic.kpis.unique_visitors, "kpi-unique"],
                      ["Page views", traffic.kpis.pageviews, "kpi-views"],
                      ["Returning", traffic.kpis.returning_visitors, "kpi-returning"],
                      ["New visitors", traffic.kpis.new_visitors, "kpi-new"],
                      ["Amazon clicks", traffic.kpis.affiliate_clicks, "kpi-clicks"],
                      ["Click-through", `${traffic.kpis.ctr}%`, "kpi-ctr"],
                    ].map(([label, val, tid]) => (
                      <div key={label} className={`sms-tweed rounded-2xl border-2 py-4 bg-[#14141b] ${tid === "kpi-unique" ? "cursor-pointer hover:bg-[#1b2417] hover:border-[#8fbf82] transition-colors relative" : ""}`} style={{ borderColor: "#5d8a52" }}
                        onClick={tid === "kpi-unique" ? () => setMapOpen(true) : undefined}
                        data-testid={tid === "kpi-unique" ? "open-visitor-list" : undefined}>
                        <div className="text-[11px] font-bold text-white/55 uppercase tracking-wide">{label}</div>
                        <div className="text-4xl font-extrabold text-white leading-tight mt-1 tabular-nums" data-testid={tid}>{val}</div>
                        {tid === "kpi-unique" && <div className="text-[10px] font-bold text-[#8fbf82] flex items-center justify-center gap-0.5 mt-0.5"><Users size={10} /> view visitors</div>}
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-white/45 -mt-2" data-testid="excluded-note">Your own admin visits ({traffic.kpis.your_own_views}){traffic.kpis.bot_hits ? ` and ${traffic.kpis.bot_hits} bot/crawler hit${traffic.kpis.bot_hits === 1 ? "" : "s"}` : ""} are excluded from these numbers — only real human visitors are counted.</p>

                  <section>
                    <h3 className="font-bold text-sm text-white mb-2" data-testid="trend-range-label">Views — {traffic.range_label || "last 7 days"} <span className="font-semibold text-white/45">(number = unique visitors)</span></h3>
                    <div className="flex items-end gap-1 h-24">
                      {traffic.daily.map((d, i) => {
                        const mx = Math.max(1, ...traffic.daily.map((x) => x.views));
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center justify-end h-full" title={`${d.date}: ${d.views} views · ${d.visitors} unique visitors`}>
                            <span className="text-[8px] font-extrabold text-[#8fbf82] leading-none mb-0.5 tabular-nums" data-testid={`day-visitors-${i}`}>{d.visitors || ""}</span>
                            <div className="w-full rounded-t" style={{ height: `${(d.views / mx) * 100}%`, minHeight: d.views ? 3 : 0, background: "#5d8a52" }} />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[9px] text-white/45 mt-1"><span>{traffic.daily[0]?.date}</span><span>{traffic.daily[traffic.daily.length - 1]?.date}</span></div>
                  </section>

                  {/* hour of day */}
                  <section>
                    <h3 className="font-bold text-sm text-white mb-2">Busiest hours (UTC)</h3>
                    <div className="flex items-end gap-0.5 h-16">
                      {traffic.hourly.map((v, i) => {
                        const mx = Math.max(1, ...traffic.hourly);
                        return <div key={i} className="flex-1 rounded-t" title={`${i}:00 — ${v} views`} style={{ height: `${(v / mx) * 100}%`, minHeight: v ? 2 : 0, background: "#b4c8ad" }} />;
                      })}
                    </div>
                    <div className="flex justify-between text-[9px] text-white/45 mt-1"><span>12a</span><span>6a</span><span>12p</span><span>6p</span><span>11p</span></div>
                  </section>

                  {/* referrers + countries */}
                  <div className="grid grid-cols-2 gap-4">
                    <section>
                      <h3 className="font-bold text-sm text-white mb-2">Where they came from</h3>
                      {traffic.top_referrers.length ? traffic.top_referrers.map((r) => (
                        <div key={r.name} className="flex justify-between text-xs py-1 border-b" style={{ borderColor: "rgba(255,255,255,.08)" }}>
                          <span className="text-white/70 truncate">{r.name}</span><span className="font-bold text-white">{r.count}</span>
                        </div>
                      )) : <p className="text-xs text-white/45">No data yet</p>}
                    </section>
                    <section>
                      <h3 className="font-bold text-sm text-white mb-2">Countries</h3>
                      {traffic.top_countries.length ? traffic.top_countries.map((c) => (
                        <div key={c.name} className="flex justify-between text-xs py-1 border-b" style={{ borderColor: "rgba(255,255,255,.08)" }}>
                          <span className="text-white/70">{c.name === "??" ? "Unknown" : c.name}</span><span className="font-bold text-white">{c.count}</span>
                        </div>
                      )) : <p className="text-xs text-white/45">No data yet</p>}
                    </section>
                  </div>

                  {/* top videos */}
                  <section>
                    <h3 className="font-bold text-sm text-white mb-2">Top videos</h3>
                    {traffic.top_videos.length ? (
                      <div className="space-y-1">
                        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[10px] font-bold text-white/45 uppercase px-2">
                          <span>Title</span><span>Views</span><span>Clicks</span><span>CTR</span>
                        </div>
                        {traffic.top_videos.map((v) => (
                          <div key={v.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-xs items-center bg-[#14141b] rounded-lg px-2 py-1.5">
                            <span className="truncate text-white font-semibold" title={v.title}>{v.title}</span>
                            <span className="text-center w-10 font-bold">{v.views}</span>
                            <span className="text-center w-10 font-bold text-[#8fbf82]">{v.clicks}</span>
                            <span className="text-center w-10 text-white/55">{v.ctr}%</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-white/45">No video views yet — share a video to start seeing traffic here.</p>}
                  </section>

                  <p className="text-[10px] text-white/45 leading-relaxed">Amazon "clicks" = visitors clicking your affiliate links (interest). Actual commissions/earnings live in your Amazon Associates dashboard — Amazon doesn't report those back to the site.</p>
                </>
              )}
            </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body)
  );
}
